import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type {
  TextBlockModifierValue,
  TextTagModifierValue,
} from "schemas/generated/fileModifiers";

import React from "react";

import { Badge } from "@replo/design-system/components/badge/Badge";
import { Button } from "@replo/design-system/components/button/Button";
import { ButtonGroup } from "@replo/design-system/components/button/ButtonGroup";
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxHeader,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTrigger,
} from "@replo/design-system/components/combobox/Combobox";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { skipToken } from "@tanstack/react-query";
import { useIframeMessaging } from "@v2/features/modifiers/hooks/useIframeMessaging";
import { useDefaultSelectedElementDesignation } from "@v2/features/modifiers/store/ModifiersStoreContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { stylesRouter } from "@v2/trpc/routes/styles";
import { LucideALargeSmall, X } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { convertFontWeightToReadableString } from "replo-runtime/shared/utils/font";
import { filterNulls } from "replo-utils/lib/array";

const vwMap: Record<string, number> = {
  h1: 6,
  h2: 5,
  h3: 4,
  h4: 3,
  p: 2,
};

// NOTE (Nick, 2025-12-18): Converts px to responsive clamp(), matching server's generateTypographyCSS
function pxToClamp(px: number, vw: number, scale = 0.15): string {
  const minRem = ((px * (1 - scale)) / 16).toFixed(3);
  const maxRem = ((px * (1 + scale)) / 16).toFixed(3);
  return `clamp(${minRem}rem, ${vw}vw, ${maxRem}rem)`;
}

interface SavedStyleSelectorProps {
  onChange: (modifiers: TextBlockModifierValue[]) => void;
  onExitSavedStyleMode: () => void;
  savedStyleMode: boolean;
}

const SavedStyleSelector: React.FC<SavedStyleSelectorProps> = ({
  onChange,
  onExitSavedStyleMode,
  savedStyleMode,
}) => {
  const projectId = useCurrentProjectId();
  const defaultDesignation = useDefaultSelectedElementDesignation();
  const { sendMessage } = useIframeMessaging();

  const { data: styleLibraryData } = stylesRouter.getStyleLibrary.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const currentStyleLibrary = styleLibraryData?.styleLibrary ?? null;

  const typography = React.useMemo(() => {
    if (!currentStyleLibrary?.typography) {
      return [];
    }

    return [
      {
        name: "Heading 1",
        key: "h1",
        htmlTag: "h1" as const,
        settings: currentStyleLibrary.typography.h1,
      },
      {
        name: "Heading 2",
        key: "h2",
        htmlTag: "h2" as const,
        settings: currentStyleLibrary.typography.h2,
      },
      {
        name: "Heading 3",
        key: "h3",
        htmlTag: "h3" as const,
        settings: currentStyleLibrary.typography.h3,
      },
      {
        name: "Heading 4",
        key: "h4",
        htmlTag: "h4" as const,
        settings: currentStyleLibrary.typography.h4,
      },
      {
        name: "Paragraph",
        key: "p",
        htmlTag: "p" as const,
        settings: currentStyleLibrary.typography.body,
      },
    ];
  }, [currentStyleLibrary?.typography]);

  const currentStyleKey = defaultDesignation?.element.tagName
    ? typography.find(
        (style) => style.htmlTag === defaultDesignation.element.tagName,
      )?.key ?? null
    : null;

  const handleStyleChange = (styleKey: string | null) => {
    const style = styleKey ? typography.find((t) => t.key === styleKey) : null;

    if (style && defaultDesignation) {
      const stableElementId = defaultDesignation.element.stableElementId;
      const timestamp = Date.now();

      // NOTE (Nick, 2025-12-18): Build CSS properties from style library for optimistic update
      const cssProperties: Record<string, string | null> = {};
      if (style.settings.size > 0) {
        const vw = vwMap[style.htmlTag] ?? 6;
        cssProperties["font-size"] = pxToClamp(style.settings.size, vw);
      }
      if (style.settings.weight > 0) {
        cssProperties["font-weight"] = String(style.settings.weight);
      }
      if (style.settings.lineHeight > 0) {
        cssProperties["line-height"] = String(style.settings.lineHeight);
      }
      if (style.settings.letterSpacing !== 0) {
        cssProperties["letter-spacing"] = `${style.settings.letterSpacing}em`;
      }
      if (style.settings.name) {
        cssProperties["font-family"] = style.settings.name;
      }

      if (Object.keys(cssProperties).length > 0) {
        sendMessage({
          type: "APPLY_OPTIMISTIC_UPDATE",
          payload: {
            modifierType: "multipleCSSProperties",
            cssProperties,
            timestamp,
            stableElementId,
          },
        });
      }

      const modifier: TextTagModifierValue = {
        type: "textTag",
        value: style.htmlTag,
      };
      onChange([modifier]);
    }
  };

  const [value, setValue] = useOverridableState(
    currentStyleKey,
    handleStyleChange,
    defaultDesignation?.element.stableElementId,
  );

  // NOTE (Kurt, 2025-07-29): Look up the display name for the currently selected typography style
  // by finding the style object that matches the current value (style key)
  const displayName = value
    ? typography.find((style) => style.key === value)?.name ?? null
    : null;

  const items: ComboboxItemWithValue[] = typography.map((style) => ({
    value: style.key,
    label: style.name,
    description: filterNulls([
      style.settings.name,
      style.settings.size ? `${style.settings.size}px` : null,
      style.settings.weight
        ? convertFontWeightToReadableString(style.settings.weight.toString())
        : null,
    ]).join(" \u00B7 "),
    size: "lg" as const,
    startEnhancer: (
      <Badge size="xl" className="text-muted">
        {style.htmlTag.toUpperCase()}
      </Badge>
    ),
  }));

  const selectedItem = value
    ? items.find((item) => item.value === value) ?? null
    : null;

  const handleComboboxValueChange = (item: ComboboxItemWithValue | null) => {
    setValue(item?.value ?? null);
  };

  const handleClearSelection = () => {
    setValue(null);
    onExitSavedStyleMode();
  };

  return (
    <ComboboxRoot
      items={items}
      value={selectedItem}
      onValueChange={handleComboboxValueChange}
      itemToStringValue={(item: ComboboxItemWithValue) => String(item.label)}
      isItemEqualToValue={(a, b) => a.value === b.value}
    >
      <ButtonGroup className="w-full" UNSAFE_className="group">
        {savedStyleMode ? (
          <ComboboxTrigger
            className="min-w-0 flex-1"
            render={
              <Button
                variant="secondary"
                size="sm"
                startEnhancer={
                  <LucideALargeSmall className="justify-self-start" />
                }
                UNSAFE_className="bg-sheet hover:bg-surface w-full rounded-r-none justify-start border-r-0 group-hover:bg-surface"
              />
            }
          >
            {displayName ?? "Select style"}
          </ComboboxTrigger>
        ) : (
          <Tooltip content="Use saved styles" triggerAsChild>
            <ComboboxTrigger
              render={
                <Button icon variant="secondary" size="sm">
                  <LucideALargeSmall />
                </Button>
              }
            />
          </Tooltip>
        )}
        {savedStyleMode && (
          <Button
            icon
            variant="tertiary"
            size="sm"
            onClick={handleClearSelection}
            UNSAFE_className="border-border border-0.5 bg-sheet! hover:bg-surface! group-hover:bg-surface!"
          >
            <X className="size-3 text-muted" />
          </Button>
        )}
      </ButtonGroup>
      <ComboboxContent
        sideOffset={8}
        side="bottom"
        align="start"
        UNSAFE_className="!min-w-64 w-full"
      >
        <ComboboxHeader>
          <ComboboxInput placeholder="Search styles..." />
        </ComboboxHeader>
        <ComboboxEmpty />
        <ComboboxList>
          {(item: ComboboxItemWithValue) => (
            <ComboboxItem key={item.value} {...item} value={item} />
          )}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
};

export default SavedStyleSelector;
